import { useCallback, useRef } from 'react'
import { ProjectsById } from '../operations'
import { ProjectFragment } from '@sketch/gql-types'

/**
 * Checks if a descendant project is a child (at any level) of an ancestor project
 */
export const isProjectDescendant = (
  ancestorId: string,
  descendantId: string,
  projectsById: ProjectsById
): boolean => {
  const ancestorProject = projectsById[ancestorId]?.project
  if (!ancestorProject) return false

  // Prevent infinite loops in case of circular references
  const visited = new Set<string>()
  let currentId: string | null = descendantId

  while (currentId && !visited.has(currentId)) {
    visited.add(currentId)

    const currentProject: ProjectFragment | undefined =
      projectsById[currentId]?.project

    if (!currentProject) return false

    // Check if we've reached the ancestor project
    if (currentProject.parentProjectIdentifier === ancestorId) {
      return true
    }

    // Move up to the parent project
    currentId = currentProject.parentProjectIdentifier
  }

  return false
}

/**
 * Hook that provides utilities for working with project hierarchy
 */
export const useIsDescendantOf = (projectsById: ProjectsById) => {
  const passDownRefs = useRef({ projectsById })
  passDownRefs.current.projectsById = projectsById

  const isDescendantOf = useCallback(
    (ancestorId: string, descendantId: string): boolean => {
      const { projectsById } = passDownRefs.current
      return isProjectDescendant(ancestorId, descendantId, projectsById)
    },
    []
  )

  return { isDescendantOf }
}

export type IsDescendantOfFn = ReturnType<
  typeof useIsDescendantOf
>['isDescendantOf']
